
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { CoachMark, Eyebrow, Heading, Row, Spacer, Stack } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import { useHasRole, useCurrentUser } from '@client/shared/auth';
import { useTutorial } from '@client/features/account';
import {
  fetchListings,
  toListingCards,
  listingsQueryKey,
} from './listings-operations.ts';
import {
  AddListingLink,
  ArchivedEmpty,
  ListingsEmpty,
  ListingsError,
  ListingsGrid,
  ListingsSkeleton,
  listingHref,
  NEW_LISTING_HREF,
} from './ListingsGrid.tsx';
import styles from './Listings.module.css';

export { listingHref, NEW_LISTING_HREF };

export function Listings({
  bookableOnly = true,
  toggleable = false,
}: { bookableOnly?: boolean; toggleable?: boolean } = {}) {
  const canCreate = useHasRole('admin', 'owner');
  const viewerId = useCurrentUser()?.user.id ?? null;

  const showArchivedToggle = toggleable && canCreate;
  const [view, setView] = useState<'live' | 'archived'>('live');
  const includeArchived = showArchivedToggle ? view === 'archived' : false;

  const query = useQuery({
    queryKey: listingsQueryKey(bookableOnly, includeArchived),
    queryFn: ({ signal }) => fetchListings(bookableOnly, includeArchived, signal),
    select: (data) => toListingCards(data, viewerId, Date.now()),
  });

  const rows =
    showArchivedToggle && view === 'archived'
      ? (query.data ?? []).filter((card) => card.archived)
      : (query.data ?? []);

  const tutorial = useTutorial();
  const addRef = useRef<HTMLSpanElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const tutorialMark =
    tutorial.flow === 'admin' ? (
      <CoachMark step="1 of 2" anchor={{ ref: addRef }} placement="left" onDismiss={tutorial.dismiss}>
        Welcome to Cheddule! Listings are what agents book open houses on. Add one with “Add new
        listing”.
      </CoachMark>
    ) : tutorial.flow === 'agent' && rows.length > 0 ? (
      <CoachMark step="1 of 2" anchor={{ ref: gridRef }} placement="top" onDismiss={tutorial.dismiss}>
        Welcome to Cheddule! Pick a listing below to book your first open house.
      </CoachMark>
    ) : null;

  return (
    <Stack gap={5}>
      <Row align="end" gap={4} className={styles.header}>
        <Stack gap={1}>
          <Eyebrow>Properties</Eyebrow>
          <Heading level={2}>Listings</Heading>
        </Stack>
        <Spacer />
        {showArchivedToggle && <ArchivedScopeToggle view={view} onSelect={setView} />}
        {canCreate && (
          <span ref={addRef} className={styles.addAnchor}>
            <AddListingLink>Add new listing</AddListingLink>
          </span>
        )}
      </Row>

      {tutorialMark}

      {query.isPending ? (
        <ListingsSkeleton />
      ) : query.isError ? (
        <ListingsError onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        showArchivedToggle && view === 'archived' ? (
          <ArchivedEmpty />
        ) : (
          <ListingsEmpty canCreate={canCreate} />
        )
      ) : (
        <div ref={gridRef}>
          <ListingsGrid rows={rows} />
        </div>
      )}
    </Stack>
  );
}

function ArchivedScopeToggle({
  view,
  onSelect,
}: {
  view: 'live' | 'archived';
  onSelect: (next: 'live' | 'archived') => void;
}) {
  return (
    <div
      className={styles.scopeToggle}
      role="radiogroup"
      aria-label="Show live or archived listings"
    >
      <button
        type="button"
        role="radio"
        aria-checked={view === 'live'}
        className={cx(styles.scopeOption, view === 'live' && styles.scopeOptionActive)}
        onClick={() => onSelect('live')}
      >
        Live
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={view === 'archived'}
        className={cx(styles.scopeOption, view === 'archived' && styles.scopeOptionActive)}
        onClick={() => onSelect('archived')}
      >
        Archived
      </button>
    </div>
  );
}
